import "reflect-metadata";
import pick from "lodash/pick";
import { Exclude, Expose, Type, plainToInstance } from "class-transformer";
// For some reason replacer is not exported in types
// @ts-expect-error
import { replacer } from "mathjs";
import type { Path } from "graphlib";

import { filter, pipe, toArray } from "@fxts/core";
import { isHeadless } from "@core/settings";
import type { CoreComponents } from "@core/components/component";
import type { EntityTag } from "@core/tags";
import { componentMask } from "@core/components/masks";
import LZString from "lz-string";
import { defaultIndexer } from "@core/systems/utils/default";
import { Vec2 } from "ogl";
import { isVec2 } from "@core/utils/misc";
import { entityIndexer } from "@core/entityIndexer/entityIndexer";
import { defaultLogger } from "@core/log";
import { PubSub } from "@core/utils/pubsub";
import { Entity, EntityComponents } from "../entity";
import { BaseSim } from "./BaseSim";
import type { System } from "../systems/system";
import { MissingEntityError } from "../errors";
import { openDb } from "../db";

const logger = defaultLogger.sub("sim");

export type ComponentAddEvent = {
  type: "addComponent";
  entity: Entity;
  component: keyof CoreComponents;
};
export type ComponentRemoveEvent = {
  type: "removeComponent";
  entity: Entity;
  component: keyof CoreComponents;
};
export type TagAddEvent = { type: "addTag"; entity: Entity; tag: EntityTag };
export type TagRemoveEvent = {
  type: "removeTag";
  entity: Entity;
  tag: EntityTag;
};
export type EntityRemoveEvent = {
  type: "removeEntity";
  entity: Entity;
  reason: string;
};
export type DestroySimEvent = {
  type: "destroy";
};
export type PhaseEvent = {
  type: "phase";
  delta: number;
  phase: "start" | "init" | "update" | "render" | "cleanup" | "end";
};
export type SpeedChangeEvent = {
  type: "speedChange";
  newSpeed: number;
};

export interface SimConfig {
  systems: System[];
}

@Exclude()
export class Sim extends BaseSim {
  delta = 0;

  @Expose()
  entityIdCounter: number = 1;
  hooks: PubSub<
    | ComponentAddEvent
    | ComponentRemoveEvent
    | TagAddEvent
    | TagRemoveEvent
    | EntityRemoveEvent
    | DestroySimEvent
    | PhaseEvent
    | SpeedChangeEvent
  >;

  @Expose()
  @Type(() => Entity)
  entities: Map<number, Entity>;
  /**
   * @deprecated
   */
  index: typeof defaultIndexer;
  paths: Record<string, Record<string, Path>>;

  constructor({ systems }: SimConfig = { systems: [] }) {
    super();

    this.entities = new Map();
    this.hooks = new PubSub();

    entityIndexer.clear();
    this.index = defaultIndexer;
    for (const index of Object.values(defaultIndexer)) {
      index.apply();
    }

    this.hooks.subscribe("addComponent", ({ entity, component }) => {
      entityIndexer.updateMask(entity);
      if (component === "position") {
        entityIndexer.updateSector(entity.requireComponents(["position"]));
      }
    });
    this.hooks.subscribe("removeComponent", ({ entity, component }) => {
      entityIndexer.updateMask(entity);
      if (component === "position") {
        entityIndexer.removeFromSectors(entity);
      }
    });
    this.hooks.subscribe("removeEntity", ({ entity, reason }) => {
      logger.log(
        `Removing entity ${entity.id} ${
          entity.cp.name?.value ??
          (entity.cp.parent
            ? `[unnamed ${
                this.getOrThrow(entity.cp.parent.id).cp.children!.entities.find(
                  (c) => c.id === entity.id
                )!.role
              }]`
            : "[unnamed]")
        } (${reason})`
      );
      entityIndexer.remove(entity);
    });
    this.hooks.subscribe("destroy", () => {
      entityIndexer.clear();
    });

    systems.forEach((system) => system.apply(this));
  }

  registerEntity = (entity: Entity) => {
    entity.id = this.entityIdCounter;
    this.entities.set(entity.id, entity);
    this.entityIdCounter += 1;
  };

  unregisterEntity = (entity: Entity, reason: string) => {
    this.hooks.publish({ type: "removeEntity", entity, reason });
    this.entities.delete(entity.id);
  };

  next = (delta: number) => {
    this.speed = this.nextSpeed;
    this.delta = delta;

    if (delta === 0) {
      this.updateTimer(delta);
      return;
    }

    this.hooks.publish({ type: "phase", delta, phase: "start" });
    this.hooks.publish({ type: "phase", delta, phase: "init" });
    this.hooks.publish({ type: "phase", delta, phase: "update" });
    this.hooks.publish({ type: "phase", delta, phase: "render" });
    this.hooks.publish({ type: "phase", delta, phase: "cleanup" });
    this.hooks.publish({ type: "phase", delta, phase: "end" });

    this.updateTimer(delta);
  };

  override setSpeed(value: number) {
    super.setSpeed(value);
    this.hooks.publish({ type: "speedChange", newSpeed: value });
  }

  init = () => {
    const settingsEntity = new Entity(this);
    settingsEntity
      .addComponent({
        name: "systemManager",
        lastStatUpdate: 0,
        lastInflationStatUpdate: 0,
      })
      .addComponent({
        name: "inflationStats",
        basketPrices: [],
      })
      .addComponent({
        name: "camera",
        zoom: 1,
        position: new Vec2(0, 0),
      });
  };

  // eslint-disable-next-line no-unused-vars
  find = (cb: (entity: Entity) => boolean): Entity | undefined => {
    for (const [, entity] of this.entities) {
      if (cb(entity)) return entity;
    }

    return undefined;
  };

  // eslint-disable-next-line no-unused-vars
  filter = (cb: (entity: Entity) => boolean): Entity[] =>
    pipe(this.entities.values(), filter(cb), toArray);

  /**
   * Get entity or `undefined`, depending on entity's existence
   * @param id Entity ID
   */
  get = <T extends Entity = Entity>(id: number): T | undefined =>
    this.entities.get(id) as T;

  /**
   * Use it when it should not be possible in any situation to get not existing
   * entity
   * @param id Entity ID
   */
  getOrThrow = <T extends Entity = Entity>(id: number): T => {
    const entity = this.entities.get(id);

    if (!entity) {
      throw new MissingEntityError(id);
    }

    return entity as T;
  };

  destroy = () => {
    this.stop();
    this.hooks.publish({ type: "destroy" });
    if (!isHeadless) {
      window.sim = undefined!;
      window.selected = undefined!;
    }
  };

  serialize = () => JSON.stringify(this, replacer);

  save = async (name: string, id?: number) => {
    const data = LZString.compress(this.serialize());
    const db = await openDb();

    const tx = db.transaction("saves", "readwrite");
    const os = tx.objectStore("saves");
    if (id) {
      os.put({ id, name, data });
    } else {
      os.add({ name, data });
    }
    tx.commit();

    return tx.done;
  };

  static async deleteSave(id: number) {
    const db = await openDb();

    const tx = db.transaction("saves", "readwrite");
    const os = tx.objectStore("saves");

    os.delete(id);

    tx.commit();

    return tx.done;
  }

  static load(config: SimConfig, data: string) {
    const save = JSON.parse(data, (_k, v) => {
      if (!v) return v;

      if (typeof v === "string" && v.startsWith("BigInt:")) {
        return BigInt(v.split("BigInt:")[1]);
      }
      if (typeof v === "object" && isVec2(v)) {
        const value = new Vec2(...v.value);
        return value;
      }

      return v;
    });

    const sim = plainToInstance(Sim, save);
    const entityMap = new Map();

    sim.entities.forEach((entity) => {
      Object.assign(
        entity.components,
        save.entities.find((e) => e.id === entity.id)!.components
      );
      entityMap.set(entity.id, entity);
      entity.sim = sim;

      entity.components = Object.assign(
        new EntityComponents(),
        entity.components
      );
      entity.componentsMask = Object.keys(entity.components).reduce(
        (mask, cp) => mask | componentMask[cp],
        BigInt(0)
      );
      entity.tags = new Set(entity.tags);
    });

    sim.entities = entityMap;

    for (const entity of sim.entities.values()) {
      entityIndexer.insert(entity);
    }
    config.systems.forEach((system) => system.apply(sim));

    return sim;
  }

  static async listSaves() {
    const db = await openDb();

    const tx = db.transaction("saves", "readonly");
    const os = tx.objectStore("saves");
    const results = os.getAll();
    await tx.done;

    return results;
  }

  toJSON() {
    return {
      ...pick(this, ["entityIdCounter", "timeOffset"]),
      entities: [...this.entities].map(([, e]) => e),
    };
  }

  toString() {
    return `Sim #${this.id}`;
  }
}

// Serialization monkeypatches

// eslint-disable-next-line func-names
(BigInt.prototype as any).toJSON = function () {
  return `BigInt:${this.toString()}`;
};

// eslint-disable-next-line func-names
(Vec2.prototype as any).toJSON = function () {
  return { isVec2: true, value: this.toArray() };
};
